import { useQuery } from '@apollo/client';
import { Badge, TBody, TD, TH, THead, TR, Table } from '@meshhub/design-tokens/ui';
import { ExternalLink, Info } from 'lucide-react';
import React, { useState } from 'react';
import {
  ActiveBadge,
  LoadingOrEmpty,
  SettingsPage,
} from '../../components/settings/SettingsKit.web.jsx';
import { PAGE_SIZE } from '../../constants/density.js';
import { formatCount, formatDateTime, formatRelative } from '../../lib/format.js';
import { USERS_QUERY } from '../../lib/queries.js';
import { KEYCLOAK_ADMIN_URL } from '../../lib/runtimeEnv.js';
import { S } from '../../lib/strings.js';

const KEYCLOAK_USERS_URL = KEYCLOAK_ADMIN_URL;

const ROLE_LABELS = { ADMIN: S.auth.roleAdmin, USER: S.auth.roleUser };

/**
 * Read-only by design.
 *
 * Keycloak owns identities and roles. Editing them here would create a second
 * source of truth that drifts the moment someone uses the Keycloak console, so
 * this screen shows what MeshHub last saw and links out for changes.
 */
export function UsersScreen() {
  const [page, setPage] = useState(1);
  const { data, loading } = useQuery(USERS_QUERY, {
    variables: { page, pageSize: PAGE_SIZE },
  });

  const users = data?.users?.items ?? [];
  const totalCount = data?.users?.totalCount ?? 0;
  const pageCount = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  return (
    <SettingsPage
      title={S.settings.sections.users}
      hint={S.contacts.count(formatCount(totalCount))}
      toolbar={
        <a
          href={KEYCLOAK_USERS_URL}
          target="_blank"
          rel="noreferrer"
          className="flex h-control items-center gap-2 rounded border border-border px-4
                     text-base text-primary hover:bg-surface-hover"
        >
          <ExternalLink size={15} aria-hidden="true" />
          {S.settings.users.openKeycloak}
        </a>
      }
    >
      <p className="mb-5 flex items-start gap-2 rounded border border-border bg-surface p-4 text-base text-text-muted">
        <Info size={16} className="mt-1 shrink-0" aria-hidden="true" />
        {S.settings.users.readOnlyNotice}
      </p>

      <LoadingOrEmpty loading={loading && users.length === 0} empty={!loading && users.length === 0} />

      {users.length > 0 && (
        <Table>
          <THead>
            <TR>
              <TH>{S.settings.users.displayName}</TH>
              <TH>{S.settings.users.role}</TH>
              <TH>{S.settings.common.status}</TH>
              <TH>{S.settings.users.firstSeen}</TH>
              <TH>{S.settings.users.lastSeen}</TH>
            </TR>
          </THead>
          <TBody>
            {users.map((user) => (
              <TR key={user.id}>
                <TD className="font-medium">{user.displayName}</TD>
                <TD>
                  <Badge
                    className={
                      user.role === 'ADMIN'
                        ? 'bg-surface-hover text-primary'
                        : 'bg-surface-hover text-text-muted'
                    }
                  >
                    {ROLE_LABELS[user.role] ?? user.role}
                  </Badge>
                </TD>
                <TD>
                  <ActiveBadge isActive={user.isActive} />
                </TD>
                <TD className="text-text-muted">{formatDateTime(user.firstSeenAt)}</TD>
                <TD className="text-text-muted" title={formatDateTime(user.lastSeenAt)}>
                  {formatRelative(user.lastSeenAt)}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}

      {pageCount > 1 && (
        <div className="mt-4 flex items-center justify-center gap-4 text-sm">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="text-primary disabled:text-text-muted"
          >
            قبلی
          </button>
          <span className="text-text-muted">
            صفحه {formatCount(page)} از {formatCount(pageCount)}
          </span>
          <button
            type="button"
            disabled={page >= pageCount}
            onClick={() => setPage((p) => p + 1)}
            className="text-primary disabled:text-text-muted"
          >
            بعدی
          </button>
        </div>
      )}
    </SettingsPage>
  );
}
